import {ChangeDetectorRef, Component, Input, ViewChild} from '@angular/core';
import {Subscription} from "rxjs";
import {SubscribedComponent} from "../subscribed.component";
import {salutations, User, UserGender, UserRole, UserStatus} from "../interfaces/user";
import {TranslateService} from "@ngx-translate/core";
import {ActivatedRoute, Router} from "@angular/router";
import {DirectoryService} from "../shared/directory.service";
import {FormBuilder, FormGroup} from "@angular/forms";
import {NbDialogService, NbLayoutScrollService, NbMenuItem, NbPopoverDirective, NbToastrService} from "@nebular/theme";
import {Globals} from "../shared/Globals";
import {UsersService} from "../shared/users.service";
import moment from 'moment';
import {ProjectService} from "../shared/project.service";
import {BSValidators} from "../shared/bsvalidators";
import {Seniority} from "../interfaces/seniority";
import {AuthService} from "../shared/auth.service";
import { HttpClient } from "@angular/common/http";
import {BaseFilePickerAdapter} from "../shared/BaseFilePickerAdapter";
import {NotifierService} from "../shared/notifier.service";
import {Holiday} from "../controls/leave-manager/leave-manager.component";
import {ColDef, ColType, ListEntry} from "../interfaces/colDef";
import {QuestionAssignment} from "../interfaces/questionAssignment";
import {Question} from "../interfaces/question";
import {Experience} from "../controls/experience-assigner/experience";
import {Department} from "../interfaces/department";
import {UserProjectAssignerComponent} from "../controls/user-project-assigner/user-project-assigner.component";
import {TableType} from "../interfaces/paged";
import {TimelineType} from "../charts/timeline-chart/timeline-chart.component";
import {SettingsService} from "../shared/settings.service";
import {MarketVacancyService} from "../shared/market-vacancy.service";
import {SuggestedVacancy} from "../interfaces/market-vacancy";
import {PopupDialogComponent} from "../dialogs/popup-dialog/popup-dialog.component";
import {MarketVacancyDetailComponent} from "../market-vacancies/market-vacancy-detail.component";
import {TitleService} from "../shared/title.service";

@Component({
    selector: 'bst-user',
    templateUrl: './user.component.html',
    styleUrls: ['../project/projects.component.scss',
        './users.component.scss'
    ],
    standalone: false
})
export class UserComponent extends SubscribedComponent{

  // Expertise cards: uniform read-mode preview cap with "+N more" expand (market-card pattern)
  public readonly EXPERTISE_CAP = 8;
  public expandedExpertise = new Set<string>();
  public toggleExpertise(key: string): void {
    this.expandedExpertise.has(key) ? this.expandedExpertise.delete(key) : this.expandedExpertise.add(key);
  }

  // Read-mode chips sorted alphabetically by the related entity's name
  public sortedExpertise(list: any[], rel: string): any[] {
    return list ? [...list].sort((a, b) => (a?.[rel]?.name || '').localeCompare(b?.[rel]?.name || '')) : list;
  }

  // resetForm() rebuilds the phone control more than once per component instance
  // (see call sites), so this is re-armed against a fresh control each time rather
  // than piling into `subs` (which only unsubscribes on destroy).
  private phoneValidatorSub: Subscription;

  public embedded: boolean;

  @Input('id') public id: number = null;
  @Input('couldedit') public couldEdit: boolean = true;
  @Input('couldarchive') public couldArchive: boolean = true;
  @Input('couldassign') public couldAssign: {start: Date, end: Date} = null;
  @Input() public showTimeline:boolean = true;

  public TableType = TableType;
  public Globals = Globals;
  public user: User = null;
  public form: FormGroup;
  public editing: boolean = false;
  public languageFluency: Record<number, number | null> = {};
  public readonly fluencyLevels: Array<{value: number | null, key: string}> = [
    {value: null, key: 'userlanguage.fluency.unset'},
    {value: 1, key: 'userlanguage.fluency.basic'},
    {value: 2, key: 'userlanguage.fluency.limited'},
    {value: 3, key: 'userlanguage.fluency.working'},
    {value: 4, key: 'userlanguage.fluency.professional'},
    {value: 5, key: 'userlanguage.fluency.native'},
  ];
  public UserStatus = UserStatus;
  public seniorities: Seniority[];
  public departments: Department[];
  public questions: Question[];
  public UserGender = UserGender;
  public UserRole = UserRole;

  private cvsub;
  public readonly salutations = salutations;
  public readonly adapter = new BaseFilePickerAdapter(this.userService, this.notifier, '/users/cv');
  public readonly today = moment().set({hour: 0, minute: 0, second: 0, millisecond: 0}).toDate();
  height: string = null;
  aiLoading: boolean = false;
  privacyExpanded: boolean = false;
  suggestedVacancies: SuggestedVacancy[] = [];
  suggestedVacanciesLoading: boolean = false;

  constructor(public userService: UsersService,
              private projectService: ProjectService,
              private translate: TranslateService,
              private router: Router,
              private route: ActivatedRoute,
              private directoryService: DirectoryService,
              private fb: FormBuilder,
              public auth:AuthService,
              private detector: ChangeDetectorRef,
              private toast: NbToastrService,
              private http: HttpClient,
              private settings: SettingsService,
              public scroll: NbLayoutScrollService,
              private notifier: NotifierService,
              private dialogService: NbDialogService,
              private marketVacancyService: MarketVacancyService,
              private titleService: TitleService) {
    super();
  }

  private async bootstrap(){

    this.loading.next(true);
    if(!this.seniorities) {
      this.seniorities = await this.directoryService.getSeniority().catch(() => null);
    }
    if(!this.departments){
      this.departments = await this.directoryService.getDepartments().catch(() => null);
    }
    if(!this.questions){
      this.questions = await this.directoryService.getQuestions().catch(() => null);
    }
    this.loading.next(false);

    if(this.id){
      await this.mapUser(this.id);
    }
    else if(this.user){
      await this.mapUser();
    }
    else {
      this.subs.push(this.route.paramMap.subscribe(async (paramMap) => {
        const id = paramMap.get('id');
        if (id) {
          this.id = parseInt(id);
          await this.mapUser(this.id);
        } else if (this.auth.user.getValue()) {
          if(this.auth.isNew){
            // Onboarding (bst-new-user) lives on /dashboard; DashboardEnabledGuard
            // lets isNew users through regardless of dashboard_enabled, since that
            // flag only gates the metrics view, not this branch.
            return await this.router.navigate(['/dashboard']);
          }
          else {
            await this.mapUser(this.auth.user.getValue().id);
          }
        }

        const edit = this.route.snapshot.queryParamMap.get('edit');
        if(this.canEdit && this.user && this.user.user_to_client?.status !== UserStatus.ARCHIVED && edit == "true"){
          this.editUser();
        }

        await this.titleService.newTitle(this.isme ? 'My profile' : null);
      }));
    }
  }

  get isDirty(): boolean {
    return this.editing && this.form.dirty && this.form.touched;
  }

  goBack(): void {
    void this.router.navigate(['/users']);
  }

  @ViewChild('adminPop', { read: NbPopoverDirective }) adminPop: NbPopoverDirective;

  /** Dispatch an admin-kebab action (called from the NbPopover template's direct
   * (click) — replaces the NbMenuService.onItemClick() Subject, which dropped
   * clicks in Chrome when the CDK overlay tore down on the same tick). */
  adminAction(action: string): void {
    this.adminPop?.hide();
    if (action === 'invite') this.invite(this.user);
    if (action === 'assign') this.assignUser();
    if (action === 'archive') this.archiveUser(this.user);
    if (action === 'unarchive') this.unarchiveUser(this.user);
    if (action === 'approve') this.approve(this.user);
    if (action === 'reject') this.reject(this.user);
  }

  ngOnInit(): void {
    // Capture popup-vs-routed mode synchronously, before async route work sets
    // id (mirrors client.component.ts). Dialog callers (table-object preview,
    // timeline-chart preview, archive-bin, user-dialog) instantiate this
    // component directly via ViewContainerRef, setting id and/or user before
    // this runs; a routed page leaves both null and resolves id from the URL.
    this.embedded = this.id != null || this.user != null;

    // If this environment is switched, refresh our user
    this.subs.push(this.auth.user.subscribe(async () => {
      if(this.user) {
        this.editing = false;
        this.user = null;
        await this.bootstrap();
      }
    }));
    this.bootstrap();
  }

  get nfuser(): boolean{
    return !(this.auth.isAdmin || this.auth.isStaffer);
  }

  get adminMenuItems(): NbMenuItem[] {
    if (!this.user || this.editing) return [];
    const status = this.user.user_to_client?.status;
    const items: NbMenuItem[] = [];

    if (status === UserStatus.ACTIVE) {
      if (this.notinvited(this.user) && this.caninvite(this.user)) {
        items.push({ title: this.translate.instant('sendinvite'), icon: 'email-outline', data: { action: 'invite' } });
      }
      if (this.couldAssign) {
        items.push({ title: this.translate.instant('assigntoproject'), data: { action: 'assign' } });
      }
      if (this.couldArchive) {
        if (items.length) items.push({ title: '', group: true });
        items.push({ title: this.translate.instant('archiveuser'), data: { action: 'archive' } });
      }
    }
    if (status === UserStatus.ARCHIVED && this.couldArchive) {
      items.push({ title: this.translate.instant('unarchiveuser'), data: { action: 'unarchive' } });
    }
    if (status === UserStatus.PENDING && !this.user.user_to_client?.invited_at) {
      items.push({ title: this.translate.instant('approveuser'), icon: 'checkmark-circle-2-outline', data: { action: 'approve' } });
      items.push({ title: this.translate.instant('rejectuser'), icon: 'close-circle-outline', data: { action: 'reject' } });
    }
    return items;
  }

  public get cv(){
    return (this.user && this.user.file_entries ? this.user.file_entries.find(o => o.tag === 'cv') : null) || null;
  }

  /**
   * This resets the user form
   * @private
   */
  private resetForm(){

    const holidays: Holiday[] = this.user ? this.user.holidays.map(o => {
      delete o.created_at;
      delete o.updated_at;
      o.dates = {
        start: Globals.fromDateString(o.start_on),
        end: Globals.fromDateString(o.end_on)
      };
      return o;
    }).sort((a: Holiday, b: Holiday) => {
      if(a.dates && b.dates){
        return Globals.fromDateString(b.dates.end).getTime() - Globals.fromDateString(a.dates.end).getTime();
      }
      return 0;
    }) : [];

    const justme = this.nfuser;
    const canedits = (this.user?.user_to_client?.freelancer || !justme);

    let controls: any = {
      name_first: [this.user?.name_first, [BSValidators.required]],
      name_last: [this.user?.name_last, [BSValidators.required]],
      salutation: [this.user?.salutation, []],
      birthdate: [Globals.fromDateStringToMoment(this.user?.birthdate), []],
      phone: [this.user?.phone, [BSValidators.phoneValid]],
      capacity: [{value: {capacity: this.user?.capacity, upcoming_capacity: this.user?.upcoming_capacity || null},
        disabled: !canedits}, [BSValidators.required]],
      holidays: [{value: holidays, disabled: !canedits}, []],
      profile_text: [this.user?.profile_text, [BSValidators.maxLength(800)]],
      linkedin_url: [this.user?.linkedin_url, [BSValidators.maxLength(500), BSValidators.pattern(/^https?:\/\/.+/)]],
      cv: [this.cv, []],
      location_home: [this.user?.location_home, []],
      career: [{value: this.user?.career, disabled: !this.isme }, []],
      categories: [this.user?.categories],
      industries: [this.user?.industries.map(o => o.industry)],
      domains: [this.user?.domains?.map(o => o.domain) || []],
      degrees: [this.user?.degrees.map(o => o.degree), []],
      developments: [this.user?.developments.map(o => o.development), []],
      languages: [this.user?.languages.map(o => o.language), []],
      markets: [this.user?.markets.map(o => o.market), []],
      skills: [this.user?.skills?.map(o => o.skill) || [], []],
      certifications: [this.user?.certifications?.map(o => o.certification) || [], []],
      analytics_opt_out: [{value: this.user?.analytics_opt_out ?? false, disabled: !this.isme}, []],
      notify_time_approval: [{value: this.user?.notify_time_approval ?? true, disabled: !this.isme}, []],
      notify_project_updates: [{value: this.user?.notify_project_updates ?? true, disabled: !this.isme}, []],
      notify_review: [{value: this.user?.notify_review ?? true, disabled: !this.isme}, []],
      notify_lifecycle: [{value: this.user?.notify_lifecycle ?? true, disabled: !this.isme}, []]
    };

    if(this.user?.user_to_client){
      const parent: ListEntry = this.user.user_to_client.parent
        ? {id: this.user.user_to_client.parent.id,
          name: this.user.user_to_client.parent.name_first + ' ' + this.user.user_to_client.parent.name_last}
        : null;

      controls = {...controls, ...{
          start_on: [{value: Globals.fromDateStringToMoment(this.user.user_to_client.start_on), disabled: !canedits}, [BSValidators.required]],
          end_on: [{value: Globals.fromDateStringToMoment(this.user.user_to_client.end_on), disabled: !canedits}, []],
          cost: [{value: this.user.user_to_client.cost, disabled: !canedits}, [BSValidators.required, BSValidators.pattern(/\d/)]],
          offered: [{value: this.user.user_to_client.offered, disabled: justme}, []],
          role: [this.user.user_to_client.role, [BSValidators.required]],
          seniority: [{value: this.user.user_to_client.seniority ? this.user.user_to_client.seniority.id : null, disabled: this.nfuser}, []],
          department: [{value: this.user.user_to_client.department ? this.user.user_to_client.department.id : null, disabled: this.nfuser}, []],
          parent_id: [{value: parent, disabled: this.nfuser}, []],
          note: [this.user.user_to_client.note, []],
          company: [{value:this.user.company, disabled:!this.isme && this.user.company}, []],
          freelancer: [this.user.user_to_client.freelancer || 0, [BSValidators.required,
            BSValidators.nextValidate(1, {company: [BSValidators.required]}, this.detector),
            BSValidators.nextValidate(0, {
              seniority: [BSValidators.required],
              offered: [BSValidators.required, BSValidators.pattern(/\d/)]
            }, this.detector)
          ]],
          visible_in_environment: [{value: this.user.user_to_client.visible_in_environment !== false, disabled: this.nfuser}, []],
          exclude_from_matching: [{value: this.user.user_to_client.exclude_from_matching === true, disabled: this.nfuser}, []],
          billable_target: [{value: this.user.user_to_client?.billable_target ?? null, disabled: !this.auth.isAdmin}, []],
          view_bill_rate: [{value: this.user.user_to_client?.view_bill_rate ?? false, disabled: !this.auth.isAdmin}, []],
          view_cost: [{value: this.user.user_to_client?.view_cost ?? false, disabled: !this.auth.isAdmin}, []],
          view_margin: [{value: this.user.user_to_client?.view_margin ?? false, disabled: !this.auth.isAdmin}, []],
          grant_invoicing: [{value: this.user.user_to_client?.grant_invoicing ?? false, disabled: !this.auth.isAdmin}, []],
          grant_quotes: [{value: this.user.user_to_client?.grant_quotes ?? false, disabled: !this.auth.isAdmin}, []],
          vehicle: [{value: this.user.user_to_client?.vehicle ?? null, disabled: !this.auth.isAdmin}, []],
          auto_approve_time: [{value: this.user.auto_approve_time ?? false, disabled: !this.user.can_configure_auto_approve}, []],
          // Multi list-adder binds objects (each {id, name}), like skills/industries. Mapped to ids on save.
          reviewers: [{value: (this.user.delegates || []), disabled: !this.auth.isAdmin}, []],
        }};

      if(this.questions) {
        const questions = {};
        for (let q of this.questions) {
          const answer = this.user.user_to_client.questions.find(o => o.Question_id === q.id);
          questions[q.id] = [answer?.answer?.data, this.nfuser && this.isme && this.user.user_to_client.freelancer
          && q.definition && q.definition.required ? [BSValidators.required] : []];
        }
        controls.questions = this.fb.group(questions);
      }
    }

    this.form = this.fb.group(controls, {
      validators: [BSValidators.dateOrder]
    });
    this.phoneValidatorSub?.unsubscribe();
    this.phoneValidatorSub = BSValidators.armPhoneValidator(this.form.get('phone'));

    this.languageFluency = {};
    for (const a of this.user?.languages ?? []) {
      if (a.language?.id) {
        this.languageFluency[a.language.id] = a.fluency ?? null;
      }
    }

    if(this.form.get('questions') && !this.isme){
      this.form.get('questions').disable();
    }

    // Check if the CV upload changes
    if(this.cvsub){
      this.cvsub.unsubscribe();
      this.cvsub = null;
    }
    this.cvsub = this.form.get('cv').valueChanges.subscribe(val => {
      if(this.user) {
        const idx = this.user.file_entries.findIndex((o) => o.tag == 'cv');
        if (idx !== -1) {
          if (val) {
            this.user.file_entries[idx] = val;
          } else {
            this.user.file_entries.splice(idx, 1);
          }
        } else {
          if (val) {
            this.user.file_entries.push(val);
          }
        }
      }
    });
  }

  private async mapUser(id: number = null) {
    this.loading.next(true);

    if(id) {
      // Toasted by errorify; a missing user falls through to the redirect below.
      this.user = await this.userService.getUser(id).catch(() => null);
    }

    if(!this.user && this.couldEdit){
      await this.router.navigate(['users']);
    }

    this.resetForm();
    this.loadSuggestedVacancies();
    this.loading.next(false);
  }

  public async saveUser() {
    this.loading.next(true);
    try {
    const categories = this.form.get('categories').value;
    const industries = this.form.get('industries').value;
    const domains = this.form.get('domains').value;
    const languages = this.form.get('languages').value;
    const degrees = this.form.get('degrees').value;
    const developments = this.form.get('developments').value;
    const markets = this.form.get('markets').value;
    const skills = this.form.get('skills').value;
    const certifications = this.form.get('certifications').value;

    let newuser: any = {
      name_first: this.form.get('name_first').value,
      name_last: this.form.get('name_last').value,
      salutation: this.form.get('salutation').value,
      birthdate: Globals.toISODate(this.form.get('birthdate').value),
      location_home: this.form.get('location_home').value,
      phone: this.form.get('phone').value?.e164Number ?? null,
      company: this.form.get('company')?.value,
      profile_text: this.form.get('profile_text').value,
      linkedin_url: this.form.get('linkedin_url').value,
      career: this.form.get('career').value,
      categories: categories ? categories : [],
      industries: industries ? industries.map(o => o.id) : [],
      domains: domains ? domains.map(o => o.id) : [],
      languages: languages ? languages.map(o => ({Language_id: o.id, fluency: this.languageFluency[o.id] ?? null})) : [],
      markets: markets ? markets.map(o => o.id) : [],
      degrees: degrees ? degrees.map(o => o.id) : [],
      developments: developments ? developments.map(o => o.id) : [],
      skills: skills ? skills.map(o => o.id) : [],
      certifications: certifications ? certifications.map(o => o.id) : [],
      analytics_opt_out: !!this.form.get('analytics_opt_out')?.value,
      notify_time_approval: !!this.form.get('notify_time_approval')?.value,
      notify_project_updates: !!this.form.get('notify_project_updates')?.value,
      notify_review: !!this.form.get('notify_review')?.value,
      notify_lifecycle: !!this.form.get('notify_lifecycle')?.value
    };

    if(this.user.user_to_client){
      const parent = this.form.get('parent_id').value;

      newuser = {...newuser, ...{
          freelancer: this.form.get('freelancer').value,
          visible_in_environment: this.form.get('visible_in_environment').value,
          exclude_from_matching: this.form.get('exclude_from_matching').value,
          start_on: Globals.toISODate(this.form.get('start_on').value),
          end_on: this.form.get('end_on') ? Globals.toISODate(this.form.get('end_on').value) : null,
          cost: this.form.get('cost').value,
          offered: this.form.get('offered').value,
          billable_target: this.form.get('billable_target')?.value ?? null,
          ...this.grantPayload(),
          ...(this.auth.isAdmin && this.form.get('vehicle')?.value ? {vehicle: this.form.get('vehicle').value} : {}),
          auto_approve_time: !!this.form.get('auto_approve_time')?.value,
          reviewers: (this.form.get('reviewers')?.value || []).map((o: any) => o.id),
          parent_id: parent ? parent.id : null,
          role: this.form.get('role').value,
          note: this.form.get('note').value,
          seniority: this.form.get('seniority')?.value,
          department: this.form.get('department')?.value
        }};

      if(this.form.get('questions')){
        const answers = this.form.get('questions').value;
        if(answers){
          newuser.questions = [];
          for(let ans in answers){
            const qu: QuestionAssignment = {
              Question_id: parseInt(ans),
              User_id: this.user.id,
              answer: answers[ans]
            };
            newuser.questions.push(qu);
          }
        }
      }
    }

    // Save capacity
    newuser.capacity = this.form.get('capacity').value;
    // newuser.capacity.start_on = Globals.toISODate(newuser.capacity.start_on);
    newuser.capacity.User_id = this.user.id;

    newuser = {...newuser, ...this.leavePayload()};

    if(newuser.career){
      newuser.career.forEach((master: Experience) => {
        master.items.forEach((item: Experience) => {
          if(item.start){
            item.start = Globals.toISODate(item.start);
          }
          if(item.end){
            item.end = Globals.toISODate(item.end);
          }
        });
        if(master.start){
          master.start = Globals.toISODate(master.start);
        }
        if(master.end){
          master.end = Globals.toISODate(master.end);
        }
      });
    }

    console.log(newuser);

    const updateduser = await this.userService.saveUser(this.user.id, newuser, "User Saved!");
    if(updateduser){
      this.user = updateduser;
      this.touched = true;
    }

    // Refresh ourselves if our name changed
    if(this.user.id === this.auth.user.getValue().id){
      await this.auth.bootstrap();
    }

    // Refresh capacity. When editing self, auth.bootstrap above triggers the
    // auth.user subscription in ngOnInit, which nulls this.user and re-fetches
    // it asynchronously — skip the form sync in that case, the re-fetch handles it.
    if (this.user) {
      this.form.get('capacity').setValue({capacity: this.user.capacity, upcoming_capacity: this.user.upcoming_capacity || null});
    }
    this.editing = false;
    } finally {
      this.loading.next(false);
    }
  }

  public async archiveUser(user: User) {
    if(await this.notifier.confirm('archivecheck', user.name_first + ' ' + user.name_last)){
      const newuser = await this.userService.changeUserStatus(user.id, UserStatus.ARCHIVED);
      await this.router.navigate(['users']);
      this.user = newuser;
    }
  }

  public editUser() {
    this.resetForm();
    this.editing = true;
  }

  public unarchiveUser(user: User) {
    Globals.loading = true;
    this.userService.changeUserStatus(user.id, UserStatus.ACTIVE).then((user) => {
      this.user = user;
      Globals.loading = false;
    });
  }

  public async deleteUser(user: User) {
    if(await this.notifier.confirm('deletecheck', user.name_first + ' ' + user.name_last, 'deleteforever', 'danger')){
      await this.userService.deleteUser(user.id);
      await this.router.navigate(['users']);
    }
  }

  public get birthyears() {
    if(this.user && this.user.birthdate){
      return moment().diff(this.user.birthdate, 'year');
    }
    return null;
  }

  public get isme(){
    return this.user && this.auth.user && this.user.id === this.auth.user.getValue().id;
  }

  public setLanguageFluency(languageId: number, level: number | null): void {
    this.languageFluency = {...this.languageFluency, [languageId]: level};
    const ctrl = this.form?.get('languages');
    if (ctrl) {
      ctrl.markAsDirty();
      ctrl.markAsTouched();
    }
  }

  public clearLanguageFluency(languageId: number): void {
    const {[languageId]: _, ...rest} = this.languageFluency;
    this.languageFluency = rest;
  }

  public fluencyKey(level: number | null | undefined): string {
    const map: Record<number, string> = {1: 'basic', 2: 'limited', 3: 'working', 4: 'professional', 5: 'native'};
    return level && map[level] ? map[level] : 'unset';
  }

  public get canEdit(): boolean{
    return this.couldEdit && (this.auth.isAdmin
      || (this.user.user_to_client?.role !== UserRole.STAFFER && this.auth.isStaffer)
      || this.isme);
  }

  public invite(user: User) {
    this.loading.next(true);
    return this.userService.reinviteUser(user.id).then((did) => {
      if(did && this.user.user_to_client){
        this.user.user_to_client.invited_at = new Date();
      }
      this.editing = false;
      this.loading.next(false);
    });
  }

  public get lastHoliday(){
    const holidays = this.form.get('holidays').value;
    if(holidays && holidays.length){
      let top: Holiday = null;
      for(let holiday of holidays){
        if(Globals.fromDateString(holiday.dates.end) < this.today){
          break;
        }
        top = holiday;
      }
      return top;
    }
    return null;
  }

  // #786: re-shape persisted holidays into the form's editing model (adds the
  // {start,end} `dates` the leave-manager + lastHoliday read), mirroring resetForm.
  private holidaysForForm(raw: Holiday[]): Holiday[] {
    return (raw || []).map(o => {
      delete o.created_at;
      delete o.updated_at;
      o.dates = {
        start: Globals.fromDateString(o.start_on),
        end: Globals.fromDateString(o.end_on)
      };
      return o;
    }).sort((a, b) => (a.dates && b.dates)
      ? Globals.fromDateString(b.dates.end).getTime() - Globals.fromDateString(a.dates.end).getTime()
      : 0);
  }

  // #786: persist a leave change made from read mode (the "Add leave" quick
  // action) without entering full profile edit. Sends a holidays-only patch —
  // the backend update() guards every field with exists(), so nothing else is
  // touched. Mirrors the holiday mapping in saveUser().
  public async saveLeaveInline(holidays: Holiday[]) {
    this.loading.next(true);
    const newuser: any = { holidays: [] };
    for(const holiday of (holidays || [])){
      newuser.holidays.push({
        id: holiday.id || null,
        name: holiday.name,
        start_on: Globals.toISODate(holiday.dates.start),
        end_on: Globals.toISODate(holiday.dates.end),
        block: holiday.block
      });
    }
    try {
      const updated = await this.userService.saveUser(this.user.id, newuser, 'Leave saved');
      if(updated){
        this.user = updated;
        this.form.get('holidays').setValue(this.holidaysForForm(updated.holidays));
      }
    } catch {
      // saveUser() already surfaced the error toast; swallow so the rejection from
      // the (quickAddSaved) binding isn't an unhandled promise rejection.
    } finally {
      // clear the spinner either way so a failed inline save can't freeze the card.
      this.loading.next(false);
    }
  }

  public caninvite(user: User){
    return !user.last_login && (!user.user_to_client?.invited_at
      || moment(user.user_to_client?.invited_at) < moment().subtract(20, 'hour'));
  }

  public notinvited(user: User){
    return user.user_to_client?.status !== this.UserStatus.ARCHIVED && !user.last_login;
  }

  public async approve(user: User) {
    this.loading.next(true);
    const did: User = await this.userService.approveUser(user.id);
    if(did){
      this.user = did;
      await this.toastit('userapproved', 'checkmark-circle-2-outline');
    }
    this.loading.next(false);
  }

  public async reject(user: User) {
    const choice = await this.notifier.confirm('userdeletecheck',
      user.name_first + ' ' + user.name_last, 'deleteforever', 'danger');
    if(choice){
      this.loading.next(true);
      const did = await this.userService.rejectUser(user.id);
      if(did){
        await this.toastit('userrejected', 'close-circle-outline');
        await this.router.navigate(['users/view/applied']);
      }
      this.loading.next(false);
    }
  }

  answer(q: Question){
    if(!this.editing && this.user.user_to_client?.questions){
      const question = this.user.user_to_client.questions.find(o => o.Question_id === q.id);
      if(question && question.answer && question.answer.data){
        return question.answer.data;
      }
    }
    return null;
  }

  private async toastit(key: string = 'success', icon:string = 'checkmark', type:string = 'success'){
    const info = await this.translate.get([key, 'success']).toPromise();
    return this.toast[type](info[key], info.success, {
      icon: icon
    });
  }

  cancel() {
    this.editing = false;
  }

  get delegateNames(): string {
    return (this.user?.delegates || []).map(d => d.name).join(', ');
  }

  // Admins hold every money grant and invoicing by role (FinancialVisibilityAuthorizer); the toggles show that, never store it.
  get targetIsAdmin(): boolean {
    return this.user?.user_to_client?.role === 3;
  }

  // Admin targets hold grant_* by role (FinancialVisibilityAuthorizer), not by the
  // stored columns: Task 2's locked toggles aren't form-bound, so there is nothing
  // to round-trip. Sending `!!stored` turned a NULL column into a stored `false`,
  // overwriting every membership row (including archived duplicates) on save.
  // Omit the fields instead; the backend already skips them when absent.
  // #1859: the server syncs holidays, deleting leave missing from the list, so
  // only send it when leave was edited here. Otherwise leave booked elsewhere
  // after the page opened would be deleted by an unrelated profile save.
  private leavePayload(): {holidays?: Partial<Holiday>[]} {
    const control = this.form.get('holidays');
    if (!control.dirty) {
      return {};
    }
    return {holidays: control.value.map((holiday: Holiday) => ({
      id: holiday.id || null,
      name: holiday.name,
      start_on: Globals.toISODate(holiday.dates.start),
      end_on: Globals.toISODate(holiday.dates.end),
      block: holiday.block
    }))};
  }

  private grantPayload(): Partial<{view_bill_rate: boolean, view_cost: boolean, view_margin: boolean, grant_invoicing: boolean, grant_quotes: boolean}> {
    if (this.targetIsAdmin) {
      return {};
    }
    return {
      view_bill_rate: this.form.get('view_bill_rate')?.value ?? false,
      view_cost: this.form.get('view_cost')?.value ?? false,
      view_margin: this.form.get('view_margin')?.value ?? false,
      grant_invoicing: this.form.get('grant_invoicing')?.value ?? false,
      grant_quotes: this.form.get('grant_quotes')?.value ?? false,
    };
  }

  get moneyAccessSummary(): string {
    if (this.targetIsAdmin) return this.translate.instant('finance.included_admin');
    const u2c = this.user?.user_to_client;
    const grants: string[] = [];
    if (u2c?.view_bill_rate) grants.push(this.translate.instant('finance.grant_bill_rate'));
    if (u2c?.view_cost) grants.push(this.translate.instant('finance.grant_cost'));
    if (u2c?.view_margin) grants.push(this.translate.instant('finance.grant_margin'));
    return grants.length ? grants.join(', ') : this.translate.instant('none');
  }

  get type(){
    return <TimelineType>this.settings.userSetting('timeline_type', TimelineType.Day);
  }

  async assignUser() {
    const assigned = await UserProjectAssignerComponent.assignmentDialog(this.directoryService, this.dialogService, this.projectService,
      this.user,
      this.couldAssign, null, this.loading);
    if(assigned){
      this.touched = true;
      return await this.mapUser(this.user.id);
    }
    return null;
  }

  /**
   * Fill profile fields with AI-extracted suggestions from CV
   */
  async fillWithAI() {
    if (this.aiLoading) return;

    this.aiLoading = true;
    try {
      const suggestions = await this.userService.suggestProfile(this.user.id);
      if (suggestions) {
        // Merge skills, certifications, developments, languages, industries, domains, degrees, and markets
        for (const field of ['skills', 'certifications', 'developments', 'languages', 'industries', 'domains', 'degrees', 'markets']) {
          if (suggestions[field]?.length) {
            const current = this.form.get(field).value || [];
            const merged = this.uniqueById([...current, ...suggestions[field]]);
            this.form.get(field).setValue(merged);
            this.form.get(field).markAsDirty();
          }
        }

        // Merge career and education into a single career array
        let career: Experience[] = this.form.get('career').value || [];
        if (suggestions.career?.length) {
          career = this.mergeExperiences(career, suggestions.career, 3);
        }
        if (suggestions.education?.length) {
          career = this.mergeExperiences(career, suggestions.education, 7);
        }
        if (suggestions.career?.length || suggestions.education?.length) {
          this.form.get('career').setValue(career);
          this.form.get('career').markAsDirty();
        }

        const info = await this.translate.get(['fillwithai.success', 'success']).toPromise();
        this.toast.success(info['fillwithai.success'], info.success, { icon: 'bulb-outline' });
      }
    } catch (e: any) {
      const errorMessage = e?.error?.message || 'fillwithai.error';
      const info = await this.translate.get([errorMessage, 'error']).toPromise();
      this.toast.danger(info[errorMessage] || errorMessage, info.error, { icon: 'alert-triangle-outline' });
    } finally {
      this.aiLoading = false;
    }
  }

  private uniqueById<T extends { id: number }>(items: T[]): T[] {
    const seen = new Set<number>();
    return items.filter(item => item?.id && !seen.has(item.id) && seen.add(item.id));
  }

  /**
   * Merge experiences avoiding duplicates by name + position (for jobs) or name + degree (for education)
   */
  private mergeExperiences(existing: Experience[], suggested: Experience[], type: number): Experience[] {
    const merged = [...existing];
    for (const item of suggested) {
      if (!item.name) continue;
      const isDuplicate = merged.some(e =>
        e.type === type &&
        e.name?.toLowerCase().trim() === item.name?.toLowerCase().trim() &&
        (type === 3 ? e.position?.toLowerCase().trim() === item.position?.toLowerCase().trim() : e.degree === item.degree)
      );
      if (!isDuplicate) merged.push(item);
    }
    return merged;
  }

  private async loadSuggestedVacancies(): Promise<void> {
    if (!(this.auth.isAdmin || this.auth.isStaffer) || !this.user) return;
    this.suggestedVacanciesLoading = true;
    try {
      this.suggestedVacancies = await this.marketVacancyService.getSuggestedVacanciesForUser(this.user.id) ?? [];
    } finally {
      this.suggestedVacanciesLoading = false;
    }
  }

  /**
   * Persist the analytics opt-out toggle. Fires inline on change so users can
   * toggle the preference without entering edit mode for the whole profile.
   */
  public async saveAnalyticsOptOut(): Promise<void> {
    if (!this.user) return;
    const optOut = !!this.form.get('analytics_opt_out')?.value;
    const updated = await this.userService.saveUser(this.user.id, { analytics_opt_out: optOut } as Partial<User>);
    if (updated) {
      this.user.analytics_opt_out = updated.analytics_opt_out;
    }
  }

  // One category, one column, one toggle — same instant-save pattern as
  // saveAnalyticsOptOut() (#795).
  public async saveNotificationPref(column: 'notify_time_approval' | 'notify_project_updates' | 'notify_review' | 'notify_lifecycle'): Promise<void> {
    if (!this.user) return;
    const value = !!this.form.get(column)?.value;
    const updated = await this.userService.saveUser(this.user.id, { [column]: value } as Partial<User>);
    if (updated) {
      this.user[column] = updated[column];
    }
  }

  // Confirm the destructive direction (excluding) before letting the toggle
  // stick; the value is persisted with the rest of the form on Save (#602).
  public async onExcludeFromMatchingChange(checked: boolean): Promise<void> {
    if (checked && !await this.notifier.confirm('excludematchingcheck',
      this.user.name_first + ' ' + this.user.name_last, 'yes', 'danger')) {
      this.form.get('exclude_from_matching')?.setValue(false);
    }
  }

  public openVacancyDetail(vacancyId: number): void {
    this.dialogService.open<PopupDialogComponent<MarketVacancyDetailComponent>>(PopupDialogComponent, {
      context: {
        comp: MarketVacancyDetailComponent,
        componentContext: { id: vacancyId, forUserId: this.user?.id }
      }
    });
  }
}
